import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Collapse,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { AuditLog, auditService, AuditLogFilters } from '../../services/auditService';

export const UserActionsTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [filtersExpanded, setFiltersExpanded] = useState(!isMobile);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filterParams: AuditLogFilters = {
        skip: page * rowsPerPage,
        take: rowsPerPage,
        ...filters
      };

      if (fromDate) filterParams.fromDate = fromDate;
      if (toDate) filterParams.toDate = toDate;

      const response = await auditService.getUserActionLogs(filterParams);
      setLogs(response.logs);
      setTotalCount(response.totalCount);
    } catch (err) {
      setError('Error loading logs');
      console.error('Error loading user action logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, fromDate, toDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (key: keyof AuditLogFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };


  const formatTimestamp = (timestamp: string) => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'dd.MM.yyyy HH:mm:ss', { locale: tr });
    } catch {
      return timestamp;
    }
  };

  const getInitials = (username: string) => {
    return username ? username.charAt(0).toUpperCase() : '?';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Filters */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.default' }}>
        {/* Filter Header */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          ...(isMobile && { cursor: 'pointer' })
        }}
        onClick={isMobile ? () => setFiltersExpanded(!filtersExpanded) : undefined}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon color="action" />
            <Typography variant="subtitle2" color="text.secondary">
              Filtreler
            </Typography>
            {isMobile && (filtersExpanded ? <ExpandLess /> : <ExpandMore />)}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Yenile">
              <IconButton onClick={loadLogs} disabled={loading} color="primary" size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Collapsible Filter Content */}
        <Collapse in={filtersExpanded}>
          <Box sx={{ p: 2, pt: 0 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Date"
                  type="datetime-local"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Date"
                  type="datetime-local"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Zone ID"
                  type="number"
                  value={filters.zoneId || ''}
                  onChange={(e) => handleFilterChange('zoneId', e.target.value ? parseInt(e.target.value) : undefined)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="User ID"
                  type="number"
                  value={filters.userId || ''}
                  onChange={(e) => handleFilterChange('userId', e.target.value ? parseInt(e.target.value) : undefined)}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && (
          <>
            {/* Mobile Card View */}
            {isMobile ? (
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
                {logs.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No user actions found yet
                    </Typography>
                  </Box>
                ) : (
                  logs.map((log) => (
                    <Paper
                      key={log.id}
                      sx={{
                        p: 2,
                        mb: 1,
                        borderRadius: 2,
                        boxShadow: 1
                      }}
                    >
                      {/* Header Row */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Avatar
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              fontSize: '0.7rem',
                              bgcolor: 'primary.main' 
                            }}
                          >
                            {getInitials(log.username || '')}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            {log.username}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'right' }}>
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </Box>

                      {/* Zone Info */}
                      {log.zoneName && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Zone: 
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 0.5, display: 'inline' }}>
                            {log.zoneName}
                          </Typography>
                        </Box>
                      )}

                      {/* Action Description */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.3 }}>
                          {log.formattedMessage}
                        </Typography>
                      </Box>

                      {/* Value Changes */}
                      {(log.previousValue || log.newValue) && (
                        <Box sx={{ 
                          backgroundColor: 'action.hover', 
                          borderRadius: 1, 
                          p: 1, 
                          display: 'flex', 
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 0.5
                        }}>
                          {log.previousValue && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                              {log.previousValue}
                            </Typography>
                          )}
                          {log.previousValue && log.newValue && (
                            <Typography variant="caption" sx={{ mx: 0.5, fontSize: '0.7rem' }}>→</Typography>
                          )}
                          {log.newValue && (
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.7rem' }}>
                              {log.newValue}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
            ) : (
              /* Desktop Table View */
              <TableContainer 
                component={Paper} 
                sx={{ 
                  flexGrow: 1, 
                  overflow: 'auto', 
                  boxShadow: 'none', 
                  borderRadius: 0,
                  '& .MuiTableCell-root': {
                    padding: '8px 16px',
                    fontSize: '0.875rem'
                  }
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 130 }}>Zaman</TableCell>
                      <TableCell sx={{ minWidth: 80 }}>User</TableCell>
                      <TableCell sx={{ minWidth: 80 }}>Zone</TableCell>
                      <TableCell sx={{ minWidth: 250 }}>Description</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                fontSize: '0.75rem',
                                bgcolor: 'primary.main' 
                              }}
                            >
                              {getInitials(log.username || '')}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {log.username}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {log.zoneName && (
                            <Tooltip title={`Zone ID: ${log.zoneId}`}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {log.zoneName}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ fontSize: '0.8rem', fontWeight: 500 }}
                          >
                            {log.formattedMessage}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(log.previousValue || log.newValue) && (
                            <Box>
                              {log.previousValue && (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {log.previousValue}
                                </Typography>
                              )}
                              {log.previousValue && log.newValue && (
                                <Typography variant="caption" sx={{ mx: 0.5 }}>→</Typography>
                              )}
                              {log.newValue && (
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {log.newValue}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {logs.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" sx={{ py: 4, color: 'text.secondary' }}>
                            No user actions found yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
              }
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};